import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DeskTable, muted, type Column } from '@/components/common/DeskTable'
import FilterBarApply from '@/components/common/FilterBarApply'
import FilterBarForm from '@/components/common/FilterBarForm'
import PageFrame from '@/components/common/PageFrame'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { GlassSelect } from '@/components/ui/glass-select'
import { backofficeApi, type AccSumRow } from '@/lib/api-client'
import { ACCSUM_WIDTH, accSumColumnsForWidth } from '@/lib/column-widths'
import { dateReadout, fmtNum } from '@/lib/format'
import { bookLabel } from '@/lib/labels'
import { FILTER_BAR } from '@/lib/ui-shell'
import { cn } from '@/lib/utils'
import { useDeskSort } from '@/hooks/useDeskSort'
import { useListPageSize } from '@/hooks/useListPageSize'

export function jakartaToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

export function plClass(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (Number.isNaN(n) || n === 0) return ''
  return n > 0 ? 'text-positive' : 'text-negative'
}

/** Profit or loss with an explicit sign, so the colour is never the only cue. */
export function PlValue({ value }: { value: number | string | null | undefined }) {
  const text = fmtNum(value)
  const n = Number(value)
  return <span className={cn(plClass(value))}>{text !== '—' && n > 0 ? `+${text}` : text}</span>
}

const num = (v: AccSumRow[keyof AccSumRow]) => muted(fmtNum(v))
const pl = (v: number | string | null | undefined) => <PlValue value={v} />

const w = ACCSUM_WIDTH
const columns: Column<AccSumRow>[] = [
  { key: 'login', sortKey: 'login', header: 'Login', sticky: true, width: w.login, mobile: 'title', cell: (r) => <span className="font-mono font-semibold">{r.login_id}</span> },
  { key: 'prev', group: 'Balance', sortKey: 'prev', sortFirst: 'desc', header: 'Previous', headerTitle: 'Previous balance', align: 'right', width: w.prev, cell: (r) => num(r.prev_balance) },
  { key: 'newbal', group: 'Balance', sortKey: 'newbal', sortFirst: 'desc', header: 'New', headerTitle: 'New balance', align: 'right', width: w.newbal, cell: (r) => num(r.new_balance) },
  { key: 'min', group: 'Margin', sortKey: 'min', sortFirst: 'desc', header: 'In', headerTitle: 'Margin in', align: 'right', width: w.min, cell: (r) => num(r.margin_in) },
  { key: 'mout', group: 'Margin', sortKey: 'mout', sortFirst: 'desc', header: 'Out', headerTitle: 'Margin out', align: 'right', width: w.mout, cell: (r) => num(r.margin_out) },
  { key: 'comm', group: 'Charges', sortKey: 'comm', sortFirst: 'desc', header: 'Commission', align: 'right', width: w.comm, cell: (r) => num(r.commission) },
  { key: 'int', group: 'Charges', sortKey: 'int', sortFirst: 'desc', header: 'Interest', align: 'right', width: w.int, cell: (r) => num(r.interest) },
  { key: 'tpl', group: 'Profit and loss', sortKey: 'tpl', sortFirst: 'desc', header: 'Trading', headerTitle: 'Trading P/L', align: 'right', width: w.tpl, cell: (r) => pl(r.trading_pl) },
  { key: 'float', group: 'Profit and loss', sortKey: 'float', sortFirst: 'desc', header: 'Floating', headerTitle: 'Floating P/L', align: 'right', width: w.float, cell: (r) => pl(r.floating_pl) },
  { key: 'dpl', group: 'Profit and loss', sortKey: 'dpl', sortFirst: 'desc', header: 'Daily', headerTitle: 'Daily P/L', align: 'right', width: w.dpl, mobile: 'field', cell: (r) => pl(r.daily_pl) },
  { key: 'equity', group: 'Account', sortKey: 'equity', sortFirst: 'desc', header: 'Equity', align: 'right', width: w.equity, mobile: 'amount', cell: (r) => num(r.equity) },
  { key: 'lot', group: 'Account', sortKey: 'lot', sortFirst: 'desc', header: 'Today lot', align: 'right', width: w.lot, mobile: 'field', cell: (r) => num(r.today_lot) },
  { key: 'ib', group: 'Other', sortKey: 'ib', header: 'IB / team', width: w.ib, className: 'overflow-hidden', mobile: 'subtitle', cell: (r) => <span className="block truncate" title={r.ib_team || undefined}>{muted(r.ib_team || '—')}</span> },
  { key: 'rate', group: 'Other', sortKey: 'rate', sortFirst: 'desc', header: 'Rate', align: 'right', width: w.rate, cell: (r) => num(r.rate) },
]

type AccFilters = { tradeDate: string; book: string; loginId: string }

function defaultFilters(): AccFilters {
  return { tradeDate: jakartaToday(), book: 'mt5', loginId: '' }
}

function parseLogin(raw: string): number | undefined {
  const t = raw.trim()
  if (!t) return undefined
  const n = Number(t)
  return Number.isInteger(n) && n > 0 ? n : undefined
}

const ACC_SORT = ['login', 'prev', 'min', 'mout', 'comm', 'int', 'tpl', 'newbal', 'float', 'equity', 'lot', 'dpl', 'ib', 'rate']

export function AccSumPage() {
  const [sp, setSp] = useSearchParams()
  const page = Number(sp.get('page') || '1')
  const pageSize = useListPageSize()
  const { sort, onSortChange, sortQuery } = useDeskSort(ACC_SORT)
  const [draft, setDraft] = useState<AccFilters>(defaultFilters)
  const [applied, setApplied] = useState<AccFilters>(defaultFilters)
  const loginId = parseLogin(applied.loginId)
  function writePage(nextPage: number, size = pageSize) {
    const cur = new URLSearchParams(sp)
    cur.set('page', String(nextPage))
    cur.set('page_size', String(size))
    setSp(cur, { replace: true })
  }

  const booksQuery = useQuery({
    queryKey: ['books'],
    queryFn: () => backofficeApi.listBooks(),
  })

  const accsumQuery = useQuery({
    queryKey: ['accsum', applied.tradeDate, applied.book, page, pageSize, loginId, sortQuery],
    queryFn: () =>
      backofficeApi.listAccSum({
        trade_date: applied.tradeDate,
        book: applied.book,
        page,
        page_size: pageSize,
        login_id: loginId,
        ...sortQuery,
      }),
    enabled: Boolean(applied.tradeDate && applied.book),
  })

  const books = booksQuery.data ?? []
  const [exportError, setExportError] = useState<string | null>(null)

  async function onExport() {
    setExportError(null)
    try {
      const blob = await backofficeApi.exportAccSum({
        trade_date: applied.tradeDate,
        book: applied.book,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `accsum_${applied.book}_${applied.tradeDate}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed')
    }
  }

  const isDirty = JSON.stringify(draft) !== JSON.stringify(applied)
  const canReset =
    JSON.stringify(draft) !== JSON.stringify(defaultFilters()) ||
    JSON.stringify(applied) !== JSON.stringify(defaultFilters())
  const loginInvalid = Boolean(draft.loginId.trim()) && parseLogin(draft.loginId) === undefined

  return (
    <PageFrame
      title="AccSum"
      description="Per-account balances, margin movements and P/L for one trade day (Asia/Jakarta)."
      actions={
        <Button variant="outline" onClick={() => void onExport()}>
          Export Excel
        </Button>
      }
    >
      {exportError ? (
        <p role="alert" className="text-sm font-semibold text-destructive">
          {exportError}
        </p>
      ) : null}
      <FilterBarForm
        className={FILTER_BAR}
        onSubmit={() => {
          if (loginInvalid) return
          setApplied(draft)
          writePage(1)
        }}
      >
        <div className="flex max-lg:flex-col max-lg:gap-3 lg:flex-row lg:flex-wrap lg:items-start lg:gap-3">
          <Field label="Trade date" className="min-w-40" hint={dateReadout(draft.tradeDate)}>
            <Input
              type="date"
              value={draft.tradeDate}
              onChange={(e) => setDraft((p) => ({ ...p, tradeDate: e.target.value }))}
            />
          </Field>
          <Field label="Book" className="min-w-32" error={booksQuery.isError ? 'Could not load books' : undefined}>
            <GlassSelect
              value={draft.book}
              onChange={(book) => setDraft((p) => ({ ...p, book }))}
              options={books.map((b) => ({ value: b.book_id, label: bookLabel(b.book_id) }))}
            />
          </Field>
          <Field
            label="Login"
            className="min-w-36"
            hint="Digits only. Leave empty for all."
            error={loginInvalid ? 'Digits only' : undefined}
          >
            <Input
              inputMode="numeric"
              value={draft.loginId}
              onChange={(e) => setDraft((p) => ({ ...p, loginId: e.target.value }))}
            />
          </Field>
          <FilterBarApply
            isDirty={isDirty && !loginInvalid}
            canReset={canReset}
            onReset={() => {
              const next = defaultFilters()
              setDraft(next)
              setApplied(next)
              writePage(1)
            }}
          />
        </div>
      </FilterBarForm>

      <DeskTable
        columns={columns}
        rows={accsumQuery.data?.items ?? []}
        rowKey={(r) => `${r.book}-${r.login_id}`}
        striped
        loading={accsumQuery.isLoading}
        error={accsumQuery.error}
        emptyTitle="No rows for this date and book"
        emptyDescription="Pick another trade date or book."
        sort={sort}
        onSortChange={onSortChange}
        visibleKeys={accSumColumnsForWidth}
        paging={{
          total: accsumQuery.data?.total ?? 0,
          page,
          pageSize,
          onPage: (p) => writePage(p),
          onPageSize: (size) => writePage(1, size),
        }}
      />
    </PageFrame>
  )
}
